import { Box } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { useContext, useMemo } from "react";
import useColors from "../../../../../api/hooks/useColors";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import {
  DashedSolidLine,
  GradientArea,
  GradientDefs,
  VerticalAnnotations,
} from "./components/ChartLayers";
import { SliceTooltip } from "./components/ChartTooltip";
import { EmptyState } from "./components/EmptyState";
import { processChartData } from "./utils/chartDataProcessor";
import { getChartConfig, getChartTheme } from "./utils/chartTheme";

// TODO: uncomment this when we have industry average
// Custom legend component with rounded corners
// const CustomLegend = ({ data, colors, sx = {} }) => {
//   return (
//     <Box
//       sx={{
//         display: "flex",
//         justifyContent: "flex-start",
//         gap: 5,
//         marginBottom: 2,
//         marginLeft: 6,
//         ...sx,
//       }}
//     >
//       {data.map((item) => (
//         <Box
//           key={item.id}
//           sx={{
//             display: "flex",
//             alignItems: "center",
//             gap: 1,
//           }}
//         >
//           <Box
//             sx={{
//               width: 12,
//               height: 12,
//               borderRadius: "3px",
//               backgroundColor: item.color,
//             }}
//           />
//           <Typography variant="caption" sx={{ color: colors.grey_600 }}>
//             {item.id}
//           </Typography>
//         </Box>
//       ))}
//     </Box>
//   );
// };

const PostureTrendChart = ({
  height = 400,
  width = "100%",
  timeRange = "30D",
  postureHeatmapCoverageTrendData,
  isLoadingPostureHeatmapCoverageTrend,
}) => {
  const colors = useColors();
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);

  const chartData = useMemo(
    () =>
      processChartData(
        postureHeatmapCoverageTrendData,
        timeRange,
        heatmapDisplayMode,
        colors.primary_200
      ),
    [
      postureHeatmapCoverageTrendData,
      timeRange,
      heatmapDisplayMode,
      colors.primary_200,
    ]
  );

  const chartConfig = getChartConfig(chartData);
  const chartTheme = getChartTheme(colors);

  if (chartData.metadata?.hasInsufficientData) {
    return <EmptyState height={height} width={width} />;
  }

  return (
    <Box
      sx={{ height, width, display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Box sx={{ height: height, width: "100%" }}>
        {/* TODO:When we have industry line, we will show the legend */}
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
            padding: "8px 16px 8px 0px",
          }}
        >
          <CustomLegend
            data={chartData.data}
            colors={colors}
            sx={{ marginBottom: 0 }}
          />
        </Box> */}
        <ResponsiveLine
          data={chartData.data}
          {...chartConfig}
          colors={colors.primary_200}
          sliceTooltip={({ slice }) => (
            <SliceTooltip slice={slice} colors={colors} />
          )}
          theme={chartTheme}
          layers={[
            () => GradientDefs({ colors }),
            "grid",
            "markers",
            "axes",
            (props) =>
              GradientArea({
                ...props,
                currentMonthIndex: chartData.metadata?.currentMonthIndex,
              }),
            "crosshair",
            "points",
            "slices",
            "mesh",
            (props) =>
              DashedSolidLine({
                ...props,
                currentMonthIndex: chartData.metadata?.currentMonthIndex,
                lastMonthDate: chartData.metadata?.lastMonthDate,
                isWeeklyView: timeRange === "30D",
              }),
            ({ xScale, yScale, height }) =>
              VerticalAnnotations({
                data: chartData,
                xScale,
                yScale,
                height,
                colors,
                timeRange,
              }),
          ]}
        />
      </Box>
    </Box>
  );
};

export default PostureTrendChart;
